import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  supportedLanguages,
  supportedRegions,
  formatCurrency as formatCurrencyUtil,
  formatDate as formatDateUtil,
  formatNumber as formatNumberUtil,
  SupportedLanguage,
  SupportedRegion,
  SupportedCurrency,
} from '../i18n';
import { detectRegionFromHostname } from '../utils/region';

interface LocaleContextType {
  language: SupportedLanguage;
  region: SupportedRegion;
  currency: SupportedCurrency;
  locale: string;
  setLanguage: (lang: SupportedLanguage) => void;
  setRegion: (region: SupportedRegion) => void;
  formatCurrency: (amount: number, currencyOverride?: SupportedCurrency) => string;
  formatDate: (date: Date | string | number, options?: Intl.DateTimeFormatOptions) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  supportedLanguages: typeof supportedLanguages;
  supportedRegions: typeof supportedRegions;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

const LOCALE_STORAGE_KEY = 'motzu_locale_preferences';

interface LocalePreferences {
  language: SupportedLanguage;
  region: SupportedRegion;
}

const getStoredPreferences = (): LocalePreferences | null => {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('Failed to load locale preferences from localStorage');
  }
  return null;
};

const storePreferences = (prefs: LocalePreferences) => {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, JSON.stringify(prefs));
  } catch (e) {
    console.warn('Failed to save locale preferences to localStorage');
  }
};

const detectDefaultRegion = (): SupportedRegion => {
  // Check domain first - .ca domains default to Canada
  const hostname = window.location.hostname;
  if (hostname.endsWith('.ca') || hostname.includes('motzulabs.ca')) {
    return 'CA';
  }
  
  // Fall back to timezone detection
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (timezone?.includes('Canada') || timezone?.includes('Toronto') || timezone?.includes('Vancouver')) {
    return 'CA';
  }
  return 'US';
};

const getLanguageFromUrl = (): SupportedLanguage | null => {
  try {
    const lang = new URLSearchParams(window.location.search).get('lang');
    if (lang && lang in supportedLanguages) {
      return lang as SupportedLanguage;
    }
  } catch (e) {
    console.warn('Failed to parse language from URL');
  }
  return null;
};

const getRegionFromUrl = (): SupportedRegion | null => {
  try {
    const region = new URLSearchParams(window.location.search).get('region')?.toUpperCase();
    if (region && region in supportedRegions) {
      return region as SupportedRegion;
    }
  } catch (e) {
    console.warn('Failed to parse region from URL');
  }
  return null;
};

/**
 * Detect if we're on the Canadian domain
 */
export const isCanadianDomain = (): boolean => {
  const hostname = window.location.hostname;
  return hostname.endsWith('.ca') || hostname.includes('motzulabs.ca');
};

interface LocaleProviderProps {
  children: ReactNode;
}

export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    const languageFromUrl = getLanguageFromUrl();
    if (languageFromUrl) {
      return languageFromUrl;
    }
    const stored = getStoredPreferences();
    if (stored?.language && stored.language in supportedLanguages) {
      return stored.language;
    }
    const browserLang = navigator.language.split('-')[0];
    if (browserLang in supportedLanguages) {
      return browserLang as SupportedLanguage;
    }
    return 'en';
  });

  const [region, setRegionState] = useState<SupportedRegion>(() => {
    const regionFromUrl = getRegionFromUrl();
    if (regionFromUrl) {
      return regionFromUrl;
    }
    // On the regional production domains the hostname is authoritative:
    // motzulabs.ca is always CA and motzulabs.com is always US. Stored
    // preferences only decide the region on hosts that serve both (local dev).
    const hostname = window.location.hostname;
    const isRegionalDomain =
      hostname.includes('motzulabs.') || hostname.includes('motzu-labs');
    if (isRegionalDomain) {
      return detectRegionFromHostname(hostname) === 'ca' ? 'CA' : 'US';
    }
    const stored = getStoredPreferences();
    if (stored?.region && stored.region in supportedRegions) {
      return stored.region;
    }
    return detectDefaultRegion();
  });

  const currency: SupportedCurrency = supportedRegions[region].currency as SupportedCurrency;
  const locale = supportedRegions[region].locale;

  const setLanguage = useCallback((lang: SupportedLanguage) => {
    setLanguageState(lang);
    i18n.changeLanguage(lang);
    storePreferences({ language: lang, region });
  }, [i18n, region]);

  const setRegion = useCallback((newRegion: SupportedRegion) => {
    setRegionState(newRegion);
    storePreferences({ language, region: newRegion });
  }, [language]);

  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [i18n, language]);

  // French is only supported in the Canadian market
  useEffect(() => {
    if (region === 'US' && language === 'fr') {
      setLanguage('en');
    }
  }, [region, language, setLanguage]);

  const formatCurrency = useCallback(
    (amount: number, currencyOverride?: SupportedCurrency) => {
      return formatCurrencyUtil(amount, currencyOverride || currency, locale);
    },
    [currency, locale]
  );

  const formatDate = useCallback(
    (date: Date | string | number, options?: Intl.DateTimeFormatOptions) => {
      return formatDateUtil(date, locale, options);
    },
    [locale]
  );

  const formatNumber = useCallback(
    (value: number, options?: Intl.NumberFormatOptions) => {
      return formatNumberUtil(value, locale, options);
    },
    [locale]
  );

  const value: LocaleContextType = {
    language,
    region,
    currency,
    locale,
    setLanguage,
    setRegion,
    formatCurrency,
    formatDate,
    formatNumber,
    supportedLanguages,
    supportedRegions,
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = (): LocaleContextType => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

export default LocaleContext;
